import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../layouts/AdminLayout';
import { buonoService, BuonoRow } from '../../services/api';
import './Statistiche.css';

const MESI = ['Tutti', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const MESI_SHORT = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const GIORNI_OPTS = ['Tutti', '1-5', '6-10', '11-15', '16-20', '21-25', '26-31'];

/** Calcola un massimo "pulito" per l'asse Y e i tick da mostrare (adattati ai dati) */
function niceYScale(maxVal: number): { max: number; ticks: number[] } {
  const raw = Math.max(maxVal, 1);
  let max: number;
  if (raw <= 5) max = Math.ceil(raw);
  else if (raw <= 10) max = Math.ceil(raw / 2) * 2;
  else max = Math.ceil(raw / 5) * 5;
  const step = max <= 5 ? 1 : max <= 10 ? 2 : 5;
  const ticks: number[] = [];
  for (let t = 0; t <= max; t += step) ticks.push(t);
  if (ticks[ticks.length - 1] !== max) ticks.push(max);
  return { max, ticks };
}

function parseCreato(creato: string): Date | null {
  const [d, m, y] = (creato || '').split('/').map(Number);
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
}

function useStats(buoni: BuonoRow[], anno: number, mese: number) {
  return useMemo(() => {
    const byMonth = Array(12).fill(0);
    const byDayRange = Array(6).fill(0);
    let total = 0;
    buoni.forEach((b) => {
      const date = parseCreato(b.creato);
      if (!date) return;
      if (date.getFullYear() !== anno) return;
      if (mese !== 0 && date.getMonth() !== mese - 1) return;
      total += 1;
      byMonth[date.getMonth()] += 1;
      const day = date.getDate();
      if (day <= 5) byDayRange[0] += 1;
      else if (day <= 10) byDayRange[1] += 1;
      else if (day <= 15) byDayRange[2] += 1;
      else if (day <= 20) byDayRange[3] += 1;
      else if (day <= 25) byDayRange[4] += 1;
      else byDayRange[5] += 1;
    });
    const monthsWithData = mese ? 1 : 12;
    const sum = byMonth.reduce((a, c) => a + c, 0);
    const media = monthsWithData ? Math.round(sum / monthsWithData) : 0;
    const massimo = Math.max(...byMonth, 0);
    return { byMonth, byDayRange, total, media, massimo };
  }, [buoni, anno, mese]);
}

const Statistiche: React.FC = () => {
  const [buoni, setBuoni] = useState<BuonoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const currentYear = new Date().getFullYear();
  const [anno, setAnno] = useState(currentYear);
  const [mese, setMese] = useState(0);
  const [giorno, setGiorno] = useState(0);

  useEffect(() => {
    let cancelled = false;
    buonoService.getBuoni().then((data) => {
      if (!cancelled) setBuoni(data);
    }).catch((err) => {
      if (!cancelled) setError(err.response?.data?.message || 'Errore nel caricamento');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const { byMonth, byDayRange, total, media, massimo } = useStats(buoni, anno, mese);
  const barScale = niceYScale(Math.max(...byMonth, 0));
  const trendScale = niceYScale(Math.max(...byDayRange, 0));

  return (
    <AdminLayout activeSection="statistiche">
      <div className="stats-page">
        <header className="stats-header">
          <h1>Statistiche</h1>
        </header>

        {loading && <div className="stats-loading">Caricamento...</div>}
        {error && <div className="stats-error">{error}</div>}

        {!loading && !error && (
          <>
            <div className="stats-filters">
              <div className="stats-filter">
                <label className="stats-filter-label">Anno</label>
                <select
                  className="stats-select"
                  value={anno}
                  onChange={(e) => setAnno(Number(e.target.value))}
                >
                  {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div className="stats-filter">
                <label className="stats-filter-label">Mese</label>
                <select
                  className="stats-select"
                  value={mese}
                  onChange={(e) => setMese(Number(e.target.value))}
                >
                  {MESI.map((m, i) => (
                    <option key={i} value={i}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="stats-filter">
                <label className="stats-filter-label">Giorno</label>
                <select
                  className="stats-select"
                  value={giorno}
                  onChange={(e) => setGiorno(Number(e.target.value))}
                >
                  {GIORNI_OPTS.map((g, i) => (
                    <option key={i} value={i}>{g}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="stats-cards">
              <div className="stats-card stats-card-pink">
                <div className="stats-card-icon stats-card-icon-gift">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 12v10H4V12M2 7h20v5H2V7zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="stats-card-content">
                  <div className="stats-card-label">Totale Buoni</div>
                  <div className="stats-card-value">{total}</div>
                </div>
              </div>
              <div className="stats-card stats-card-white">
                <div className="stats-card-icon stats-card-icon-blue">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="stats-card-content">
                  <div className="stats-card-label">Media per Periodo</div>
                  <div className="stats-card-value">{media}</div>
                </div>
              </div>
              <div className="stats-card stats-card-white">
                <div className="stats-card-icon stats-card-icon-green">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 17l4-4 4 4 6-8 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="stats-card-content">
                  <div className="stats-card-label">Massimo Raggiunto</div>
                  <div className="stats-card-value">{massimo}</div>
                </div>
              </div>
            </div>

            <div className="stats-charts">
              <div className="stats-chart-card">
                <h2 className="stats-chart-title">Statistiche {anno} - Grafico a barre</h2>
                <div className="stats-bar-chart">
                  <svg viewBox="0 0 420 240" preserveAspectRatio="xMidYMid meet">
                    <text x="18" y="130" textAnchor="middle" className="stats-chart-axis-text" transform="rotate(-90 18 130)">n° buoni</text>
                    {barScale.ticks.map((tick, i) => {
                      const y = 200 - (tick / barScale.max) * 150;
                      return <line key={i} x1="50" y1={y} x2="400" y2={y} stroke="#f3e8ec" strokeWidth="1" />;
                    })}
                    {byMonth.map((val, i) => {
                      const h = barScale.max ? (val / barScale.max) * 150 : 0;
                      const x = 58 + i * 28;
                      return (
                        <rect key={i} x={x} y={200 - h} width="20" height={h} fill="#F6339A" rx="2" />
                      );
                    })}
                    {MESI_SHORT.map((m, i) => (
                      <text key={i} x={68 + i * 28} y="222" textAnchor="middle" className="stats-chart-axis-text">{m}</text>
                    ))}
                    {barScale.ticks.map((tick, i) => {
                      const y = 200 - (tick / barScale.max) * 150;
                      return <text key={i} x="42" y={y + 4} textAnchor="end" className="stats-chart-axis-text">{tick}</text>;
                    })}
                  </svg>
                </div>
              </div>
              <div className="stats-chart-card">
                <h2 className="stats-chart-title">Statistiche {anno} - Trend</h2>
                <div className="stats-trend-chart">
                  <svg viewBox="0 0 420 240" preserveAspectRatio="xMidYMid meet">
                    {trendScale.ticks.map((tick, i) => {
                      const y = 200 - (tick / trendScale.max) * 150;
                      return <line key={i} x1="50" y1={y} x2="400" y2={y} stroke="#f3e8ec" strokeWidth="1" />;
                    })}
                    {(() => {
                      const points = byDayRange.map((val, i) => {
                        const y = 200 - (trendScale.max ? (val / trendScale.max) * 150 : 0);
                        const x = 90 + i * 52;
                        return `${x},${y}`;
                      }).join(' ');
                      return (
                        <>
                          <polyline points={points} fill="none" stroke="#F6339A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          {byDayRange.map((_, i) => {
                            const y = 200 - (trendScale.max ? (byDayRange[i] / trendScale.max) * 150 : 0);
                            const x = 90 + i * 52;
                            return <circle key={i} cx={x} cy={y} r="5" fill="#F6339A" />;
                          })}
                        </>
                      );
                    })()}
                    {GIORNI_OPTS.slice(1).map((g, i) => (
                      <text key={i} x={116 + i * 52} y="222" textAnchor="middle" className="stats-chart-axis-text">{g}</text>
                    ))}
                    {trendScale.ticks.map((tick, i) => {
                      const y = 200 - (tick / trendScale.max) * 150;
                      return <text key={i} x="42" y={y + 4} textAnchor="end" className="stats-chart-axis-text">{tick}</text>;
                    })}
                  </svg>
                </div>
              </div>
            </div>

            <footer className="stats-footer">
              © 2021 All rights reserved Estetica Cruciani
            </footer>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default Statistiche;
